import {
  IDiaryService,
  IDiarySchedule,
  IBlockout,
  ISectionLayoutItem,
  IServiceLayoutItem,
  BlockoutRepeatTypes,
  IEditScheduleService,
  IDiaryScheduleRawTimes,
} from 'shared-types/index';
import EventsLookupService from 'external-services/events-lookup-service/index';
import DateUtilsService from 'external-services/date-utils-service/index';
import BlockoutsService from 'external-services/blockouts-service/blockouts.service';
import {
  ILookupService,
  IScheduleExtensionService,
} from 'external-services/schedule-extension-service/types';
import { cloneDeep, sortBy } from 'lodash-es';
import { NS } from './types';
import moment from 'moment-timezone';

class ScheduleExtensionService {
  __exposedForTests = {
    sortServicesData: this.sortServicesData,
    getServiceLayoutsForStandardServices: this.getServiceLayoutsForStandardServices,
    modifyServiceLayoutsForEvents: this.modifyServiceLayoutsForEvents,
  };

  async addEvents(schedule: IDiarySchedule, date: Date): Promise<IDiarySchedule> {
    const events = await EventsLookupService.getEventsOnDate(date);
    if (!schedule.isOpen && events.length) {
      // you were orinally ment to be closedon this day
      // open and only have the event(s) listed
      return {
        ...schedule,
        isOpen: true,
        services: [],
      };
    }
    schedule.services = schedule.services.concat(events as any);
    return schedule;
  }

  async addBlockouts(schedule: IDiarySchedule, date: Date): Promise<IDiarySchedule> {
    const blockouts: IBlockout[] = await BlockoutsService.getBlockoutsOnDate(date);

    const sectionIds = Array.from(
      new Set(
        schedule.services
          .map((service) => {
            return service.sectionLayouts.map(({ sectionId }) => {
              return sectionId;
            });
          })
          .flat()
      )
    );

    const blockoutsWithSections = blockouts.map((blockout) => {
      if (blockout.repeatType === BlockoutRepeatTypes.Close) {
        return {
          ...blockout,
          blockedSections: sectionIds,
        };
      }
      return blockout;
    });
    schedule.blockouts = blockoutsWithSections;
    return schedule;
  }

  private sortServicesData(scheduleServices: IDiaryService[]): {
    events: IScheduleExtensionService[];
    standardServices: IScheduleExtensionService[];
    usedSectionIds: string[];
  } {
    const events: IScheduleExtensionService[] = [];
    const standardServices: IScheduleExtensionService[] = [];
    const usedSectionIdsSet: Set<string> = new Set();

    scheduleServices.forEach((scheduleService, serviceIndex) => {
      // find out what sections are used
      const sectionLookUp: ILookupService = {};
      scheduleService.sectionLayouts.forEach((sectionLayout) => {
        sectionLookUp[sectionLayout.sectionId] = sectionLayout;
        usedSectionIdsSet.add(sectionLayout.sectionId);
      });
      const newService = {
        ...scheduleService,
        sectionLookUp,
        serviceIndex,
      };
      scheduleService?.type === 'event'
        ? events.push(newService)
        : standardServices.push(newService);
    });
    return {
      events,
      standardServices,
      usedSectionIds: Array.from(usedSectionIdsSet),
    };
  }

  private getServiceLayoutsForStandardServices(
    usedSectionIds: string[],
    standardServices: IScheduleExtensionService[],
    date: Date
  ): IServiceLayoutItem[] {
    return standardServices
      .map((standardService) => {
        const startMin = DateUtilsService.minsBetweenDates(date, standardService.startTime);
        const endMin = DateUtilsService.minsBetweenDates(date, standardService.endTime);
        const totalMin = endMin - startMin;
        return usedSectionIds.map((sectionId) => {
          const sectionLayoutItem: ISectionLayoutItem = standardService.sectionLookUp[sectionId];
          const sectionOpen: boolean = !!sectionLayoutItem;
          const sectionNotUsed = sectionLayoutItem?.sectionNotUsed || false;
          const sectionClosed = sectionLayoutItem?.sectionClosed || false;

          return {
            serviceIndex: standardService.serviceIndex,
            serviceId: standardService._id,
            sectionId,
            layoutId: sectionOpen ? sectionLayoutItem.layoutId : '',
            sectionOpen,
            sectionClosed,
            sectionNotUsed,
            name: standardService.name,
            startTime: standardService.startTime,
            endTime: standardService.endTime,
            startMin,
            endMin,
            totalMin,
          };
        });
      })
      .flat();
  }

  private modifyServiceLayoutsForEvents(
    eventObj: IDiaryService,
    serviceLayoutsFromServices: IServiceLayoutItem[],
    startMin: number,
    endMin: number
  ): IServiceLayoutItem[] {
    const eventObjSectionIds = eventObj.sectionLayouts.map(({ sectionId }) => sectionId);

    return serviceLayoutsFromServices.reduce((accumulator, serviceLayout) => {
      const updatedServiceLayouts: IServiceLayoutItem[] = [];
      if (eventObjSectionIds.includes(serviceLayout.sectionId)) {
        // The serviceLayout used events section

        if (
          eventObj.startTime.getTime() <= getTime(serviceLayout.startTime) &&
          eventObj.endTime.getTime() >= getTime(serviceLayout.endTime)
        ) {
          // The event over laps the complete serviceLayouts
          // remove the entire serviceLayouts
          // updatedServiceLayouts = [] // return nothing, ignore this ServiceLayout
        } else if (
          eventObj.startTime.getTime() > getTime(serviceLayout.startTime) &&
          eventObj.endTime.getTime() < getTime(serviceLayout.endTime)
        ) {
          // The events spits a serviceLayout modify the end time and add a new one

          updatedServiceLayouts.push({
            // original serviceLayout
            ...serviceLayout,
            endTime: eventObj.startTime,
            endMin: startMin,
            totalMin: serviceLayout.endMin - serviceLayout.startMin,
          });
          const newService = {
            ...serviceLayout,
            startTime: eventObj.endTime,
            startMin: endMin,
            totalMin: serviceLayout.endMin - endMin,
          };
          updatedServiceLayouts.push(newService);
        } else if (
          eventObj.startTime.getTime() > getTime(serviceLayout.startTime) &&
          eventObj.startTime.getTime() < getTime(serviceLayout.endTime)
        ) {
          // The event start after the serviceLayout does
          // change the serviceLayout end time
          updatedServiceLayouts.push({
            ...serviceLayout,
            endTime: eventObj.startTime,
            endMin: startMin,
            totalMin: serviceLayout.endMin - serviceLayout.startMin,
          });
        } else if (
          eventObj.endTime.getTime() < getTime(serviceLayout.endTime) &&
          eventObj.endTime.getTime() > getTime(serviceLayout.startTime)
        ) {
          // The event ends before the service does
          // change the serviceLayout start time
          updatedServiceLayouts.push({
            ...serviceLayout,
            startTime: eventObj.endTime,
            startMin: endMin,
            totalMin: serviceLayout.endMin - serviceLayout.startMin,
          });
        } else {
          updatedServiceLayouts.push(serviceLayout);
        }
      } else {
        updatedServiceLayouts.push(serviceLayout);
      }
      return accumulator.concat(updatedServiceLayouts);
    }, []);
  }

  private insertEventsIntoServiceLayouts(
    eventObj: IScheduleExtensionService,
    startMin: number,
    endMin: number
  ): IServiceLayoutItem[] {
    return eventObj.sectionLayouts.map((sectionLayout) => {
      const { serviceIndex, startTime, endTime, name } = eventObj;
      return {
        serviceIndex,
        sectionId: sectionLayout.sectionId,
        serviceId: eventObj._id,
        layoutId: sectionLayout.layoutId,
        sectionOpen: Boolean(sectionLayout.layoutId),
        startTime,
        endTime,
        eventId: eventObj._id,
        name,
        startMin,
        endMin,
        sectionNotUsed: sectionLayout?.sectionNotUsed,
        sectionClosed: sectionLayout?.sectionClosed,
        totalMin: endMin - startMin,
      };
    });
  }

  addServicesSections(schedule: IDiarySchedule, date: Date): IDiarySchedule {
    const { events, standardServices, usedSectionIds } = this.sortServicesData(schedule.services);
    const serviceLayoutsFromServices = this.getServiceLayoutsForStandardServices(
      usedSectionIds,
      standardServices,
      date
    );
    const serviceLayoutsFromEvents = events.reduce((result, eventObj) => {
      const startMin = DateUtilsService.minsBetweenDates(date, eventObj.startTime);
      const endMin = DateUtilsService.minsBetweenDates(date, eventObj.endTime);

      const insertEventsServiceLayouts = this.insertEventsIntoServiceLayouts(
        eventObj,
        startMin,
        endMin
      );
      const modifiedServiceLayouts = this.modifyServiceLayoutsForEvents(
        eventObj,
        serviceLayoutsFromServices,
        startMin,
        endMin
      );

      return [...result, ...modifiedServiceLayouts, ...insertEventsServiceLayouts];
    }, []);

    schedule.serviceLayouts = events.length ? serviceLayoutsFromEvents : serviceLayoutsFromServices;
    return schedule;
  }

  convertScheduleToFullTimes(
    scheduleRawTimes: IDiaryScheduleRawTimes,
    timeZoneId?: string,
    date?: Date
  ): IDiarySchedule {
    const services: IDiaryService[] = this.convertServiceTimesToDates(
      scheduleRawTimes.services,
      timeZoneId,
      date
    );
    return { ...scheduleRawTimes, services };
  }

  /**
   * used to be called 'scheduleService.orderServices' in Diary. Either timezoneId or date must be passed
   * @param services
   * @param timezoneId
   * @param date
   */
  convertServiceTimesToDates(
    services: IEditScheduleService[],
    timeZoneId?: string,
    date?: Date
  ): IDiaryService[] {
    if (!date && !timeZoneId) {
      throw Error(`${NS} -> convertServiceTimesToDates: must pass either date or timezoneId`);
    }

    if (!date) {
      date = DateUtilsService.getClearDate(null, timeZoneId);
    }

    let convertedServices: IDiaryService[] = cloneDeep(services as unknown as IDiaryService[]);
    convertedServices.forEach((service) => {
      service.startTime = DateUtilsService.timeToDate(
        getOnlyTimeStrFromFullDate(service.startTime),
        date
      );
      service.lastBookingTime = DateUtilsService.timeToDate(
        getOnlyTimeStrFromFullDate(service.lastBookingTime),
        date
      );
      service.endTime = DateUtilsService.timeToDate(
        getOnlyTimeStrFromFullDate(service.endTime),
        date
      );
    });

    // make sure the services are in the correct order
    convertedServices = sortBy(convertedServices, ['startTime']);
    // services = $filter('orderBy')(services, 'startTime');
    return convertedServices;
  }

  serviceHasRawDate({ services }: IDiarySchedule | IDiaryScheduleRawTimes): boolean {
    const isRawTimes = services?.length && typeof services[0].startTime === 'string';
    return isRawTimes;
  }
}

const getOnlyTimeStrFromFullDate = (date: Date) => {
  if (date instanceof Date) {
    const timeOnly = moment(date).format('HH:mm');
    return timeOnly;
  } else {
    return date;
  }
};

const getTime = (date: string | Date): number => {
  return typeof date === 'string' ? new Date(date).getTime() : date.getTime();
};

const instance = new ScheduleExtensionService();
export default instance as ScheduleExtensionService;
